import { useEffect, useState } from 'react';

/**
 * A phone in either orientation: below Tailwind's `sm` breakpoint when portrait, by its short
 * height and finger pointer when landscape (667-932px wide). Same query as the `phone:`
 * variant and the 16px field rule of index.css.
 */
export const PHONE_MEDIA_QUERY = '(max-width: 639px), (pointer: coarse) and (max-height: 500px)';

/**
 * A screen too short for a court above its lists: a phone held landscape, or a short window.
 * No pointer condition, unlike `phone`: the layout problem is the height, whatever the pointer.
 */
export const SHORT_LANDSCAPE_MEDIA_QUERY = '(orientation: landscape) and (max-height: 500px)';

const matches = (media: string): boolean =>
  typeof window !== 'undefined' &&
  typeof window.matchMedia === 'function' &&
  window.matchMedia(media).matches;

function useMediaQuery(media: string): boolean {
  const [isMatching, setIsMatching] = useState(() => matches(media));

  useEffect(() => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(media);
    const onChange = () => setIsMatching(query.matches);
    onChange();
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, [media]);

  return isMatching;
}

/**
 * For layout values CSS classes cannot reach, such as Recharts axis widths and margins
 * (plain numbers given to the SVG).
 */
export function useIsPhone(): boolean {
  return useMediaQuery(PHONE_MEDIA_QUERY);
}

/** For a layout that swaps components, not only classes (the two-team set setup court). */
export function useIsShortLandscape(): boolean {
  return useMediaQuery(SHORT_LANDSCAPE_MEDIA_QUERY);
}

/** Shortens an axis label to fit a narrow axis; the full text stays in the bar tooltip. */
export function truncateAxisLabel(label: string, maxChars: number): string {
  return label.length > maxChars ? `${label.slice(0, maxChars - 1)}…` : label;
}

function sharedPrefixLength(a: string, b: string): number {
  let i = 0;
  while (i < a.length && i < b.length && a[i] === b[i]) i += 1;
  return i;
}

/**
 * The part of `label` that holds its first difference with the closest other label:
 * the start when it differs early ("Jean-Pier…"), else a window around the difference
 * ("…t-Martin" vs "…t-Morin").
 */
function distinctivePart(label: string, others: readonly string[], maxChars: number): string {
  const diffAt = Math.max(...others.map((o) => sharedPrefixLength(label, o)));
  if (diffAt < maxChars - 1) return truncateAxisLabel(label, maxChars);
  const width = maxChars - 1;
  const start = Math.max(1, Math.min(diffAt - Math.floor(width / 2), label.length - width));
  const window = label.slice(start, start + width);
  return start + width < label.length ? `…${window.slice(0, width - 1)}…` : `…${window}`;
}

/**
 * Shortens every label of one axis so that two players stay distinguishable:
 * "Alexandre Dupont" → "A. Dupont" when a first name can be initialled. Labels that
 * would still read the same show where they differ ("Jean-Pier…" vs "Jean-Paul…",
 * "…t-Martin" vs "…t-Morin"). A numbered suffix, which could pass for a jersey
 * number, is only the last resort.
 */
export function shortenAxisLabels(labels: readonly string[], maxChars: number): Map<string, string> {
  const unique = Array.from(new Set(labels));
  const short = new Map<string, string>();
  for (const label of unique) {
    if (label.length <= maxChars) {
      short.set(label, label);
      continue;
    }
    const [first, ...rest] = label.trim().split(/\s+/);
    const initialled = rest.length > 0 ? `${first.charAt(0)}. ${rest.join(' ')}` : label;
    short.set(label, truncateAxisLabel(initialled, maxChars));
  }

  const byShort = new Map<string, string[]>();
  for (const [label, s] of short) byShort.set(s, [...(byShort.get(s) ?? []), label]);
  for (const group of byShort.values()) {
    if (group.length < 2) continue;
    for (const label of group) {
      if (label.length <= maxChars) continue;
      short.set(label, distinctivePart(label, group.filter((o) => o !== label), maxChars));
    }
  }

  // Sorted, so the same player keeps the same number whatever the order of the data.
  const taken = new Set<string>();
  for (const label of [...unique].sort()) {
    let s = short.get(label) ?? label;
    for (let n = 2; taken.has(s); n += 1) {
      s = `${truncateAxisLabel(label, maxChars - String(n).length - 1)} ${n}`;
    }
    taken.add(s);
    short.set(label, s);
  }
  return short;
}

// "12 " in front of the name: two digits and a space.
const JERSEY_PREFIX_CHARS = 3;

/**
 * Same as `shortenAxisLabels`, with the jersey in front when known ("7 A. Dupont"): on a phone
 * the number tells two close short names apart at a glance (VOL-277).
 */
export function shortenNumberedAxisLabels(
  labels: readonly string[],
  maxChars: number,
  jerseys: ReadonlyMap<string, number>,
): Map<string, string> {
  if (!labels.some((label) => jerseys.has(label))) return shortenAxisLabels(labels, maxChars);
  const short = shortenAxisLabels(labels, maxChars - JERSEY_PREFIX_CHARS);
  const numbered = new Map<string, string>();
  for (const [label, s] of short) {
    const jersey = jerseys.get(label);
    numbered.set(label, jersey === undefined ? s : `${jersey} ${s}`);
  }
  return numbered;
}
